import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';

import CaretDownIcon from '@/assets/icons/caret-down.svg?react';
import { CardViewSkeleton } from '@/components/elements/_skeletons/card-view.skeleton';
import { TableViewSkeleton } from '@/components/elements/_skeletons/table-view.skeleton';
import { GeneralCard } from '@/components/elements/general-card/general-card.component';
import { T } from '@/components/i18n/message.i18n.component';
import { GridTableLayout } from '@/components/layout/grid-table/grid-table.layout.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { LifecycleStatus, Product, ProductGroup } from '@/gql/graphql';
import { useProducts } from '@/hooks/use-products/use-products.hook';
import { useToast } from '@/hooks/use-toast.hook';
import { useUser } from '@/hooks/use-user/use-user.hook';
import {
  isProductCopyEnabled,
  isProductDeleteEnabled,
  isProductEditEnabled,
  isProductStatusChangeEnabled,
} from '@/lib/product.utils';
import { dialogTranslations, productPageMessages, toastTranslations } from '@/pages/products/all/products.messages';
import { allProducts, getProductGroup, removeProduct, updateProduct } from '@/pages/products/products.requests';
import { TemplateForm } from '@/pages/templates/all/components/template-form/template-form.component';
import { templateFormMessages } from '@/pages/templates/all/components/template-form/template-form.messages';
import { useDialogStore } from '@/store/dialog.store';
import { useIntl } from 'react-intl';
import { productCardLocators } from './components/product-card.locators';
import { productCardMessages } from './components/product-card.messages';
import { ProductForm } from './components/product-form/product-form';
import { ProductTable } from './components/product-table.component';
import { ProductsNoResults } from './products.no-results';

export const ProductsPage = () => {
  const t = useIntl();
  const { guid } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { groupId } = useParams();

  const { data } = useQuery<{ productGroup: ProductGroup }>(getProductGroup, { variables: { id: groupId } });

  const {
    products,
    loading,
    search,
    filter,
    productsPageView,
    setProductsPageView,
    handleFilterChange,
    handleSearchChange,
  } = useProducts({
    initialProducts: data?.productGroup?.members,
  });

  const [editProductMutation] = useMutation(updateProduct);
  const [deleteProductMutation] = useMutation(removeProduct);

  const { openDialog, setDialogLoading, closeDialog } = useDialogStore((state) => ({
    loading: state.loading,
    openDialog: state.openDialog,
    closeDialog: state.closeDialog,
    setDialogLoading: state.setLoading,
  }));

  const handleProductAddDialog = () => {
    openDialog({
      type: 'custom',
      title: <T {...dialogTranslations.addProductTitle} />,
      element: (
        <ProductForm
          context="product"
          initialValues={{ groupId: data?.productGroup.id }}
          afterAdd={(result) => {
            const id = result.data?.createProduct.id;
            navigate(`/product/${id}/pre-tailoring`);
          }}
        />
      ),
    });
  };

  const openEditDialog = (product: Product) => {
    openDialog({
      type: 'custom',
      title: <T {...dialogTranslations.editProductTitle} />,
      element: (
        <ProductForm
          context="product"
          edit
          initialValues={{
            id: product.id,
            name: product.name,
            description: product.description,
            lifecycleStatus: product.lifecycleStatus,
            version: product.version,
            productIdentifier: product.productIdentifier || '',
            projectIdentifier: product.projectIdentifier || '',
            groupId: product.group?.id || '',
          }}
        />
      ),
    });
  };

  const openConfirmationDialog = (product: Product) => {
    openDialog({
      type: 'confirmation',
      item: { id: product.id, name: product.name },
      onConfirm: async () => {
        setDialogLoading(true);
        await deleteProductMutation({
          variables: {
            id: product.id,
          },
          refetchQueries: [{ query: getProductGroup, variables: { id: groupId } }],
        });
        closeDialog();
        toast({
          description: <T {...toastTranslations.deleteProductToast} />,
          variant: 'default',
        });
      },
    });
  };

  const openTemplateDialog = (product: Product) => {
    openDialog({
      type: 'custom',
      title: <T {...templateFormMessages.addTemplateDialogTitle} />,
      element: (
        <TemplateForm
          initialValues={{
            copyAnswersFrom: product.id,
          }}
          afterAdd={() => {
            navigate('/templates');
          }}
        />
      ),
    });
  };

  const onProductStatusChange = async (product: Product) => {
    if (product.lifecycleStatus === LifecycleStatus.New || product.lifecycleStatus === LifecycleStatus.InProgress) {
      openDialog({
        type: 'confirmation',
        title: <T {...dialogTranslations.retireProductTitle} />,
        confirmationText: t.formatMessage(dialogTranslations.retireProductDescription, {
          name: product.name,
        }),
        item: { id: product.id, name: product.name },
        onConfirm: async () => {
          setDialogLoading(true);
          await editProductMutation({
            variables: {
              updateProductInput: {
                id: product.id,
                name: product.name,
                description: product.description,
                version: product.version,
                productIdentifier: product.productIdentifier,
                projectIdentifier: product.projectIdentifier,
                lifecycleStatus: LifecycleStatus.Retired,
                groupId: product?.group?.id,
              },
            },
            refetchQueries: [{ query: allProducts }],
          });
          closeDialog();
          toast({
            description: <T {...toastTranslations.retireProductToast} />,
            variant: 'default',
          });
        },
      });
    } else {
      await editProductMutation({
        variables: {
          updateProductInput: {
            id: product.id,
            name: product.name,
            description: product.description,
            version: product.version,
            productIdentifier: product.productIdentifier,
            projectIdentifier: product.projectIdentifier,
            lifecycleStatus: LifecycleStatus.InProgress,
            groupId: product?.group?.id,
          },
        },
        refetchQueries: [{ query: allProducts }],
      });
      toast({
        description: <T {...toastTranslations.activateProductToast} />,
        variant: 'default',
      });
    }
  };

  const handleProductNavigation = (id: string) => {
    navigate(`/product/${id}/pre-tailoring`);
  };

  const handleViewChange = () => {
    setProductsPageView(productsPageView === 'grid' ? 'table' : 'grid');
  };

  const handleNavigation = () => {
    navigate('/products');
  };

  return (
    <GridTableLayout
      context="products"
      loading={loading}
      loadingComponent={
        productsPageView === 'grid' ? (
          <CardViewSkeleton data-testid="product-page-loading-skeleton" />
        ) : (
          <TableViewSkeleton data-testid="product-page-loading-skeleton" />
        )
      }
      noResults={!loading && products.length === 0}
      noResultsComponent={
        <ProductsNoResults
          search={search}
          loading={loading}
          products={products}
          onProductAdd={handleProductAddDialog}
        />
      }
      actionBarConfig={{
        searchPlaceholder: t.formatMessage(productPageMessages.searchBarPlaceholder),
        filterBarText: <T {...productPageMessages.filterBarText} />,
        filterBarOptionAllProducts: <T {...productPageMessages.filterBarOptionAllProducts} />,
        filterBarOptionMyProducts: <T {...productPageMessages.filterBarOptionMyProducts} />,
        tableViewButton: <T {...productPageMessages.tableViewButton} />,
        gridViewButton: <T {...productPageMessages.gridViewButton} />,
        selectedView: productsPageView,
        handleViewChange,
        filter,
        handleFilterChange,
        handleSearchChange,
      }}
      navigationBarConfig={{
        productsTitle: (
          <div className="flex flex-row justify-center items-center gap-x-1">
            <CaretDownIcon className="rotate-90" />
            {data?.productGroup?.name}
          </div>
        ),
        addButtonTitle: <T {...productPageMessages.addProduct} />,
        addButtonAction: handleProductAddDialog,
        onNavClick: handleNavigation,
      }}
    >
      {productsPageView === 'grid' &&
        products.map((product) => (
          <GeneralCard
            id={product.id}
            key={product.id}
            context="product"
            title={product.name}
            description={product.description}
            updatedAt={product.updatedAt}
            onCardClick={handleProductNavigation}
            menu={[
              {
                name: 'edit',
                disabled: !isProductEditEnabled(guid, product),
                label: <T {...productCardMessages.editProduct} />,
                handleClick: () => openEditDialog(product),
              },
              {
                name: 'use-as-new-template',
                disabled: !isProductCopyEnabled(product),
                handleClick: () => openTemplateDialog(product),
                label: <T {...productCardMessages.useAsNewTemplate} />,
              },
              {
                name: 'status-change',
                disabled: !isProductStatusChangeEnabled(guid, product),
                handleClick: () => onProductStatusChange(product),
                label:
                  product.lifecycleStatus === LifecycleStatus.Retired ? (
                    <T {...productCardMessages.setAsActive} />
                  ) : (
                    <T {...productCardMessages.setAsRetired} />
                  ),
              },
              {
                name: 'delete',
                disabled: !isProductDeleteEnabled(guid, product),
                handleClick: () => openConfirmationDialog(product),
                label: <T {...productCardMessages.deleteProduct} />,
                red: true,
              },
            ]}
            rows={[
              {
                name: 'status',
                label: <T {...productCardMessages.status} />,
                value: (
                  <Pill
                    data-testid={productCardLocators.productCardStatusValue(product.id)}
                    productStatus={product.lifecycleStatus}
                    text={product.lifecycleStatus}
                  />
                ),
              },
              {
                name: 'version',
                label: <T {...productCardMessages.version} />,
                value: product.version,
              },
            ]}
          />
        ))}
      {productsPageView === 'table' && (
        <ProductTable
          data={products}
          onRowClick={handleProductNavigation}
          handleEdit={openEditDialog}
          handleUseAsNewTemplate={openTemplateDialog}
          handleDelete={openConfirmationDialog}
          handleStatusChange={onProductStatusChange}
        />
      )}
    </GridTableLayout>
  );
};
